// Trust layer: derive where a food's nutrient data came from.
//
// Plate OS stores provenance with every food — this module only reads it; it
// never recomputes nutrients. Keep this package dependency-free so both the
// Vite web app and Expo mobile app use the same classification rules.

export type ProvenanceKind =
  | "usda"
  | "branded"
  | "restaurant"
  | "ai_estimated"
  | "user";

export interface FoodProvenance {
  kind: ProvenanceKind;
  /** AI confidence (0..1) when a live Neural Engine result supplied one. */
  confidence?: number;
}

/** Coarse origin of the confirmed nutrient snapshot, never a model identifier. */
export type NutritionSource = "database" | "ai_estimated";

export function safeNutritionSource(value: unknown): NutritionSource | undefined {
  return value === "database" || value === "ai_estimated" ? value : undefined;
}

/** Unknown/legacy notes are intentionally unlabeled, not inferred from a catalog. */
export function loggedNutritionSource(notes: string | null | undefined): NutritionSource | undefined {
  try {
    return safeNutritionSource(JSON.parse(notes ?? "{}")?.nutritionSource);
  } catch {
    return undefined;
  }
}

/** Minimal shape we read from a food record (Orval `Food` is a superset). */
export interface ProvenanceFood {
  brand?: string | null;
  barcode?: string | null;
  notes?: string | null;
  nutrients71?: string | Record<string, unknown> | null;
  neuralConfidence?: number | null;
  userId?: number | null;
}

function parseNutrients(
  nutrients71: ProvenanceFood["nutrients71"],
): Record<string, unknown> | null {
  if (!nutrients71) return null;
  if (typeof nutrients71 === "object")
    return nutrients71 as Record<string, unknown>;
  try {
    return JSON.parse(nutrients71);
  } catch {
    return null;
  }
}

function hasUsdaImportSlugs(n71: Record<string, unknown> | null): boolean {
  if (!n71) return false;
  const slugs = (n71 as { _usda_import_slugs?: unknown })._usda_import_slugs;
  return Array.isArray(slugs) && slugs.length > 0;
}

/**
 * Classify a food's nutrient source. `opts.confidence` lets callers pass a
 * live Neural Engine confidence (e.g. from a grocery analysis) which only
 * applies to the `ai_estimated` tier.
 */
export function getFoodProvenance(
  food: ProvenanceFood | null | undefined,
  opts?: { confidence?: number | null },
): FoodProvenance {
  const notes = (food?.notes ?? "").toString();
  const n71 = parseNutrients(food?.nutrients71);

  // Restaurant attribution wins over branded: Nutritionix menu items carry a
  // brand (the chain) but are prepared meals, not packaged products.
  if (/nutritionix/i.test(notes)) {
    return { kind: "restaurant" };
  }

  if (/usda/i.test(notes) || hasUsdaImportSlugs(n71)) {
    return { kind: "usda" };
  }

  const hasBrand = !!(food?.brand && food.brand.trim());
  if (
    food?.barcode ||
    hasBrand ||
    /open food facts/i.test(notes) ||
    /fooddata central/i.test(notes)
  ) {
    return { kind: "branded" };
  }

  // Mirrors classifyStoredFoodSource() on the api-server: a food owned by a
  // user with no USDA/branded markers is the user's own entry ("You added").
  if (food?.userId != null) {
    return { kind: "user" };
  }

  const confidence =
    opts?.confidence ??
    (typeof food?.neuralConfidence === "number"
      ? food.neuralConfidence
      : undefined) ??
    undefined;
  return { kind: "ai_estimated", confidence: confidence ?? undefined };
}

/**
 * Map an API provenance `source` label (returned by food-search and
 * barcode-lookup) to a trust tier. Search/barcode results carry an explicit
 * `source` string rather than the raw food fields, so this is preferred over
 * `getFoodProvenance` for those responses. Returns `null` for unknown labels.
 */
export function provenanceFromSource(
  source: string | null | undefined,
  opts?: { confidence?: number | null },
): FoodProvenance | null {
  const s = (source ?? "").toString().toLowerCase().trim();
  if (!s) return null;

  if (s === "restaurant" || /nutritionix/.test(s)) {
    return { kind: "restaurant" };
  }
  if (
    s === "openfoodfacts" ||
    s === "fdc" ||
    s === "branded" ||
    s === "barcode" ||
    /open food facts/.test(s) ||
    /fooddata central/.test(s)
  ) {
    return { kind: "branded" };
  }
  if (/usda/.test(s)) return { kind: "usda" };
  if (s === "ai" || s === "ai_estimated") {
    return { kind: "ai_estimated", confidence: opts?.confidence ?? undefined };
  }
  if (s === "user" || s === "database" || s === "manual") {
    return { kind: "user" };
  }
  return null;
}

/** Bucket a 0..1 confidence into a coarse band for labels/coloring. */
export function confidenceBand(confidence: number): "high" | "medium" | "low" {
  if (confidence >= 0.85) return "high";
  if (confidence >= 0.7) return "medium";
  return "low";
}

/** The storage/calculation contract is always mass-normalized. Display servings
 * are conversions into this basis, never an alternative nutrient basis. */
export const NUTRIENT_BASIS = "per_100g" as const;
export type NutrientBasis = typeof NUTRIENT_BASIS;
export type NutrientUnit = "kcal" | "kJ" | "g" | "mg" | "mcg";
export type VerificationLevel =
  | "authoritative"
  | "provider_verified"
  | "label_verified"
  | "user_entered"
  | "ai_estimated"
  | "unverified";

export interface NutrientDefinition {
  key: string;
  unit: NutrientUnit;
  requiredForCoreCompleteness: boolean;
}

export const CANONICAL_NUTRIENTS = [
  ["energy_kcal", "kcal", true], ["energy_kj", "kJ", false],
  ["protein", "g", true], ["total_fat", "g", true],
  ["total_carbohydrate", "g", true], ["dietary_fiber", "g", true],
  ["sugar", "g", true], ["added_sugar", "g", false],
  ["saturated_fat", "g", true], ["trans_fat", "g", false],
  ["monounsaturated_fat", "g", false], ["polyunsaturated_fat", "g", false],
  ["cholesterol", "mg", true], ["sodium", "mg", true],
  ["potassium", "mg", true], ["calcium", "mg", true], ["iron", "mg", true],
  ["magnesium", "mg", false], ["phosphorus", "mg", false], ["zinc", "mg", false],
  ["copper", "mg", false], ["manganese", "mg", false], ["selenium", "mcg", false],
  ["vitamin_a_rae", "mcg", true], ["vitamin_c", "mg", true],
  ["vitamin_d", "mcg", true], ["vitamin_e", "mg", false], ["vitamin_k1", "mcg", false],
  ["thiamin_b1", "mg", false], ["riboflavin_b2", "mg", false],
  ["niacin_b3", "mg", false], ["vitamin_b6", "mg", false],
  ["folate_b9", "mcg", false], ["vitamin_b12", "mcg", true],
  ["choline", "mg", false], ["water", "g", false],
] as const satisfies readonly (readonly [string, NutrientUnit, boolean])[];

export const NUTRIENT_REGISTRY: Readonly<Record<string, NutrientDefinition>> =
  Object.freeze(Object.fromEntries(CANONICAL_NUTRIENTS.map(([key, unit, requiredForCoreCompleteness]) =>
    [key, { key, unit, requiredForCoreCompleteness }])));

export type NutrientValue = number | null;
export type NutrientMap = Record<string, NutrientValue>;

export interface NutritionTruth {
  basis: NutrientBasis;
  nutrients: NutrientMap;
  provenance: { kind: ProvenanceKind; provider?: string; providerId?: string };
  verificationLevel: VerificationLevel;
  completeness: number;
  missing: string[];
  lastReviewedAt: string | null;
  confidence: number | null;
}

const round = (value: number): number => Math.round((value + Number.EPSILON) * 1e6) / 1e6;

/** Strictly parse nutrient maps. Null and absent values remain unknown; only
 * explicit finite numeric zero is zero. Metadata keys are ignored. */
export function normalizeNutrients(input: unknown): NutrientMap {
  if (!input || typeof input !== "object" || Array.isArray(input)) return {};
  const result: NutrientMap = {};
  for (const [key, raw] of Object.entries(input as Record<string, unknown>)) {
    if (key.startsWith("_")) continue;
    if (raw === null) result[key] = null;
    else if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0) result[key] = round(raw);
  }
  return result;
}

export function scaleNutrients(per100g: NutrientMap, grams: number): NutrientMap {
  if (!Number.isFinite(grams) || grams < 0) throw new Error("grams must be a finite non-negative number");
  const factor = grams / 100;
  return Object.fromEntries(Object.entries(per100g).map(([key, value]) =>
    [key, value === null ? null : round(value * factor)]));
}

/** Sums known observations and marks a nutrient unknown if any component is
 * unknown or absent. This prevents sparse ingredients from silently becoming 0. */
export function combineNutrients(components: readonly NutrientMap[]): NutrientMap {
  const keys = new Set(components.flatMap(component => Object.keys(component)));
  const out: NutrientMap = {};
  for (const key of keys) {
    const values = components.map(component => component[key]);
    out[key] = values.some(value => value == null)
      ? null
      : round((values as number[]).reduce((sum, value) => sum + value, 0));
  }
  return out;
}

export function recipePer100g(
  ingredients: readonly { nutrientsPer100g: NutrientMap; quantityG: number }[],
  cookedYieldG: number,
): NutrientMap {
  if (!Number.isFinite(cookedYieldG) || cookedYieldG <= 0) throw new Error("cookedYieldG must be positive");
  const totals = combineNutrients(ingredients.map(item => scaleNutrients(item.nutrientsPer100g, item.quantityG)));
  return scaleNutrients(totals, 10000 / cookedYieldG);
}

/** Convert a pre-cook recipe yield to its cooked mass. Nutrients are conserved,
 * so reducing cooked mass increases the per-100g concentration. */
export function cookedYieldGrams(yieldG: number, cookingLossPercent: number): number {
  if (!Number.isFinite(yieldG) || yieldG <= 0) throw new Error("yieldG must be positive");
  if (!Number.isFinite(cookingLossPercent) || cookingLossPercent < 0 || cookingLossPercent >= 100) {
    throw new Error("cookingLossPercent must be between 0 and 100");
  }
  return round(yieldG * (1 - cookingLossPercent / 100));
}

export function servingGrams(quantity: number, unit: "g" | "kg" | "oz" | "lb" | "serving", servingSizeG?: number): number {
  if (!Number.isFinite(quantity) || quantity < 0) throw new Error("quantity must be non-negative");
  const factors = { g: 1, kg: 1000, oz: 28.349523125, lb: 453.59237 } as const;
  if (unit === "serving") {
    if (!servingSizeG || servingSizeG <= 0) throw new Error("servingSizeG is required for serving units");
    return round(quantity * servingSizeG);
  }
  return round(quantity * factors[unit]);
}

export function nutritionCompleteness(nutrients: NutrientMap): { score: number; missing: string[] } {
  const core = CANONICAL_NUTRIENTS.filter(([, , required]) => required).map(([key]) => key);
  const missing = core.filter(key => nutrients[key] == null);
  return { score: round((core.length - missing.length) / core.length), missing };
}

export function canonicalFoodFingerprint(food: { name: string; brand?: string | null; barcode?: string | null }): string {
  if (food.barcode?.trim()) return `barcode:${food.barcode.replace(/\D/g, "")}`;
  const clean = (value: string) => value.normalize("NFKD").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return `text:${clean(food.brand ?? "")}|${clean(food.name)}`;
}

const verificationRank: Record<VerificationLevel, number> = {
  authoritative: 6, provider_verified: 5, label_verified: 4,
  user_entered: 3, ai_estimated: 2, unverified: 1,
};

export function rankNutritionRecord(record: Pick<NutritionTruth, "verificationLevel" | "completeness" | "confidence">): number {
  const completeness = Math.max(0, Math.min(1, record.completeness));
  const confidence = record.confidence == null ? 0.5 : Math.max(0, Math.min(1, record.confidence));
  return verificationRank[record.verificationLevel] * 100 + completeness * 20 + confidence;
}